
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ApprovalPost } from '@/types/approval';
import { toast } from '@/lib/toast';

export function useApprovals() {
  const [approvals, setApprovals] = useState<ApprovalPost[]>([]);
  const [archivedApprovals, setArchivedApprovals] = useState<ApprovalPost[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchApprovals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user');
        return;
      }

      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id, permissions')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
        const permissions = collaboratorData.permissions as any;
        if (!permissions?.approvals) {
          setApprovals([]);
          return;
        }
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        setApprovals([]);
        return;
      }

      const { data, error } = await supabase
        .from('approval_posts')
        .select('*')
        .eq('company_id', targetCompanyId)
        .neq('status', 'archived')
        .is('deleted_at', null) // Excluir aprovações deletadas
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transformar os dados para o tipo ApprovalPost correto
      const transformedApprovals: ApprovalPost[] = (data || []).map(approval => ({
        ...approval,
        status: approval.status as 'pending' | 'approved' | 'rejected' | 'archived',
        post_type: (approval.post_type || 'static') as 'static' | 'carousel' | 'reels',
        visible_to: Array.isArray(approval.visible_to) ? approval.visible_to.map(id => String(id)) : []
      }));
      
      // Filtrar aprovações visíveis
      const visibleApprovals = transformedApprovals.filter(approval => {
        if (approval.user_id === user.id) return true;
        if (approval.visible_to && Array.isArray(approval.visible_to)) {
          return approval.visible_to.includes(user.id);
        }
        return collaboratorData ? true : false;
      });
      
      setApprovals(visibleApprovals);
    } catch (error: any) {
      console.error('Error fetching approvals:', error);
      toast.error("Erro ao carregar aprovações");
    }
  };

  const fetchArchivedApprovals = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user');
        return;
      }

      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id, permissions')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
        const permissions = collaboratorData.permissions as any;
        if (!permissions?.approvals) {
          setArchivedApprovals([]);
          return;
        }
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        setArchivedApprovals([]);
        return;
      }

      const { data, error } = await supabase
        .from('approval_posts')
        .select('*')
        .eq('company_id', targetCompanyId)
        .eq('status', 'archived')
        .is('deleted_at', null) // Excluir aprovações deletadas
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transformar os dados para o tipo ApprovalPost correto
      const transformedApprovals: ApprovalPost[] = (data || []).map(approval => ({
        ...approval,
        status: approval.status as 'pending' | 'approved' | 'rejected' | 'archived',
        post_type: (approval.post_type || 'static') as 'static' | 'carousel' | 'reels',
        visible_to: Array.isArray(approval.visible_to) ? approval.visible_to.map(id => String(id)) : []
      }));
      
      // Filtrar aprovações visíveis
      const visibleApprovals = transformedApprovals.filter(approval => {
        if (approval.user_id === user.id) return true;
        if (approval.visible_to && Array.isArray(approval.visible_to)) {
          return approval.visible_to.includes(user.id);
        }
        return collaboratorData ? true : false;
      });
      
      setArchivedApprovals(visibleApprovals);
    } catch (error: any) {
      console.error('Error fetching archived approvals:', error);
      toast.error("Erro ao carregar aprovações arquivadas");
    }
  };

  const createApproval = async (approvalData: Partial<ApprovalPost>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Buscar company_id do usuário
      let targetCompanyId: string | null = null;

      // Verificar se é colaborador
      const { data: collaboratorData } = await supabase
        .from('collaborators')
        .select('company_id')
        .eq('collaborator_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (collaboratorData) {
        targetCompanyId = collaboratorData.company_id;
      } else {
        const { data: userData } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .single();
        targetCompanyId = userData?.company_id || null;
      }

      if (!targetCompanyId) {
        throw new Error('Empresa não encontrada');
      }

      // Buscar todos os usuários da empresa para adicionar na visibilidade
      const { data: collaborators } = await supabase
        .from('collaborators')
        .select('collaborator_id')
        .eq('company_id', targetCompanyId)
        .eq('status', 'active');

      // Buscar proprietários da empresa
      const { data: companyData } = await supabase
        .from('companies')
        .select('owner_ids')
        .eq('id', targetCompanyId)
        .single();

      // Criar lista de usuários que podem ver a aprovação
      const visibleTo = [user.id];
      
      // Adicionar colaboradores ativos
      if (collaborators) {
        collaborators.forEach(collab => {
          if (!visibleTo.includes(collab.collaborator_id)) {
            visibleTo.push(collab.collaborator_id);
          }
        });
      }
      
      // Adicionar proprietários da empresa
      if (companyData?.owner_ids && Array.isArray(companyData.owner_ids)) {
        companyData.owner_ids.forEach((ownerId: string) => {
          if (!visibleTo.includes(ownerId)) {
            visibleTo.push(ownerId);
          }
        });
      }

      // Generate a unique slug for the approval
      const slug = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const baseDomain = getBaseDomain();
      const shareableLink = `${baseDomain}/approve/${slug}`;

      // Garantir que os campos obrigatórios estão preenchidos
      const dataToInsert = {
        ...approvalData,
        user_id: user.id,
        company_id: targetCompanyId,
        status: 'pending',
        title: approvalData.title || '',
        client_name: approvalData.client_name || '',
        platform: approvalData.platform || 'instagram',
        slug: slug,
        shareable_link: shareableLink,
        visible_to: visibleTo
      };

      const { data, error } = await supabase
        .from('approval_posts')
        .insert(dataToInsert)
        .select()
        .single();

      if (error) throw error;
      
      await fetchApprovals();
      toast.success("Aprovação criada com sucesso!");
      return data;
    } catch (error: any) {
      console.error('Error creating approval:', error);
      toast.error("Erro ao criar aprovação");
      throw error;
    }
  };

  const updateApprovalStatus = async (id: string, status: string, feedback?: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          status, 
          feedback,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      await fetchApprovals();
      toast.success(`Post ${status === 'approved' ? 'aprovado' : 'rejeitado'} com sucesso!`);
    } catch (error: any) {
      console.error('Error updating approval status:', error);
      toast.error("Erro ao atualizar status da aprovação");
    }
  };

  const archiveApproval = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          status: 'archived',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      await fetchApprovals();
      await fetchArchivedApprovals();
      toast.success("Post arquivado com sucesso!");
      return true;
    } catch (error: any) {
      console.error('Error archiving approval:', error);
      toast.error("Erro ao arquivar aprovação");
      return false;
    }
  };

  const deleteApproval = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      await fetchApprovals();
      await fetchArchivedApprovals();
      toast.success("Post excluído com sucesso!");
      return true;
    } catch (error: any) {
      console.error('Error deleting approval:', error);
      toast.error("Erro ao excluir aprovação");
      return false;
    }
  };

  const restoreApproval = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ 
          deleted_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      await fetchApprovals();
      await fetchArchivedApprovals();
      toast.success("Post restaurado com sucesso!");
    } catch (error: any) {
      console.error('Error restoring approval:', error);
      toast.error("Erro ao restaurar aprovação");
    }
  };

  const resubmitApproval = async (approval: ApprovalPost, newImageUrl: string, newCaption: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({
          image_url: newImageUrl,
          caption: newCaption,
          status: 'pending',
          feedback: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', approval.id);

      if (error) throw error;
      
      await fetchApprovals();
      toast.success("Post reenviado para aprovação!");
      return true;
    } catch (error: any) {
      console.error('Error resubmitting approval:', error);
      toast.error("Erro ao reenviar aprovação");
      return false;
    }
  };

  const refreshApprovals = async () => {
    await Promise.all([fetchApprovals(), fetchArchivedApprovals()]);
  };

  const getBaseDomain = () => {
    return window.location.origin;
  };

  const getApprovalBySlug = async (slug: string) => {
    try {
      const { data, error } = await supabase
        .from('approval_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as ApprovalPost;
    } catch (error) {
      console.error('Error fetching approval by slug:', error);
      return null;
    }
  };

  const loadArchivedApprovals = async () => {
    await fetchArchivedApprovals();
    return archivedApprovals;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchApprovals(), fetchArchivedApprovals()]);
      setLoading(false);
    };

    loadData();
  }, []);

  return {
    approvals,
    archivedApprovals,
    loading,
    isLoading: loading,
    fetchApprovals,
    fetchArchivedApprovals,
    createApproval,
    updateApprovalStatus,
    archiveApproval,
    deleteApproval,
    restoreApproval,
    resubmitApproval,
    refreshApprovals: async () => {
      await Promise.all([fetchApprovals(), fetchArchivedApprovals()]);
    },
    getBaseDomain: () => window.location.origin,
    getApprovalBySlug: async (slug: string) => {
      try {
        const { data, error } = await supabase
          .from('approval_posts')
          .select('*')
          .eq('slug', slug)
          .single();

        if (error) throw error;
        return data as ApprovalPost;
      } catch (error) {
        console.error('Error fetching approval by slug:', error);
        return null;
      }
    },
    loadArchivedApprovals: async () => {
      await fetchArchivedApprovals();
      return archivedApprovals;
    }
  };
}

// Re-export ApprovalPost for convenience
export type { ApprovalPost } from '@/types/approval';
